import {
  GetSalaryStructureGraphQLResponse,
  UpdateSalaryStructureGraphQLRequest,
} from 'api/gql-utils/salaryStructures/types';
import { ComponentSettings, EarningsComponentType } from 'components/SalaryComponents/types';
import { SalaryStructure, SalaryStructureComponent } from 'components/SalaryStructure/types';
import { paiseAmountToRupee, rupeeAmountToPaise, uniqueId } from 'utils/Numbers';
import { deletablePredfinedComponentTypes, predefinedComponentTypes } from './constants';

type SalaryStructureErrors = {
  components?: string;
  percentageSum?: string;
};

export const validateSalaryStructureComponents = (components: SalaryStructureComponent[]) => {
  const errors: SalaryStructureErrors = {};

  if (components.length === 0) {
    errors.components = 'At least one component is required';
  }

  const percentageComponents = components.filter(
    (component) => component.valueType === 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_CTC',
  );

  const percentageSum = percentageComponents.reduce((sum, component) => {
    const percentage = component.formula ? parseFloat(component.formula) : 0;
    return sum + percentage;
  }, 0);

  if (percentageSum > 100) {
    errors.percentageSum = 'The sum of percentage components exceeds 100%';
  }

  const combinedErrorsString = Object.values(errors).join('\n');

  return { errors, combinedErrorsString };
};

export const parseGetSalaryStructureResponse = (
  response: GetSalaryStructureGraphQLResponse,
): GetSalaryStructureGraphQLResponse => {
  return {
    ...response,
    payrollGetSalaryStructure: {
      ...response.payrollGetSalaryStructure,
      data: {
        ...response.payrollGetSalaryStructure.data,
        salaryStructureComponents:
          response.payrollGetSalaryStructure.data.salaryStructureComponents.map((component) => {
            const valueType = component.valueType;
            const formula =
              valueType === 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_FIXED'
                ? paiseAmountToRupee(parseInt(component.formula ?? '0')).toString()
                : component.formula;
            return {
              ...component,
              formula,
            };
          }),
      },
    },
  };
};

export const generateUpdateSalaryStructurePayload = (
  salaryStructure: SalaryStructure,
  salaryStructureComponents: SalaryStructureComponent[],
): UpdateSalaryStructureGraphQLRequest => {
  const filteredComponents = salaryStructureComponents
    .filter(
      (salaryStructureComponent) => salaryStructureComponent.salaryComponent.name.trim() !== '',
    )
    .map((salaryStructureComponent) => {
      const salaryComponent = salaryStructureComponent.salaryComponent;
      const { settings, category } = salaryComponent;
      const specificSettings: ComponentSettings = {
        behaviour: settings.behaviour,
      };
      if (category === 'EARNINGS_CATEGORY') {
        specificSettings.earningDetails = settings.earningDetails;
      } else if (category === 'DEDUCTIONS_CATEGORY') {
        specificSettings.deductionDetails = settings.deductionDetails;
      } else if (category === 'NON_PAYABLE_BENEFITS_CATEGORY') {
        specificSettings.nonPayableBenefitsDetails = settings.nonPayableBenefitsDetails;
      } else if (category === 'PERQUISITES_CATEGORY') {
        specificSettings.perquisiteDetails = settings.perquisiteDetails;
      } else if (category === 'BONUS_CATEGORY') {
        specificSettings.bonusDetails = settings.bonusDetails;
      }
      const valueType = salaryStructureComponent.valueType;
      const formula =
        valueType === 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_FIXED'
          ? rupeeAmountToPaise(parseInt(salaryStructureComponent.formula ?? '0')).toString()
          : salaryStructureComponent.formula;
      return {
        id: salaryStructureComponent.id,
        valueType,
        salaryComponentId: salaryComponent.id,
        formula,
      };
    });

  return {
    input: {
      name: salaryStructure.name,
      description: salaryStructure.description,
      id: salaryStructure.id,
      type: salaryStructure.type,
      salaryStructureComponents: filteredComponents,
    },
  };
};

export const generatePlaceholderComponent = (): SalaryStructureComponent => {
  const id = uniqueId();
  const salaryComponentId = uniqueId();
  return {
    id,
    valueType: 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_FIXED',
    salaryComponentId,
    salaryComponent: {
      id: salaryComponentId,
      name: '', // keeping name empty, to remove the component from the list during update
      description: '',
      isPredefined: false,
      isActive: true,
      source: 'USER',
      category: 'EARNINGS_CATEGORY',
      settings: {
        behaviour: {
          displayBehaviour: {
            displayName: '',
          },
        },
        earningDetails: {
          payType: 'fixed',
        },
      },
    } as EarningsComponentType,
  };
};

export const isComponentDeletable = (component: SalaryStructureComponent) => {
  const componentType = component.salaryComponent.componentType || 'COMPONENT_TYPE_NA';
  const isPredefined = predefinedComponentTypes.includes(componentType);
  if (isPredefined) {
    return deletablePredfinedComponentTypes.includes(componentType);
  } else {
    return true;
  }
};
